import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { api, setToken } from "../lib/api";
import { MaskedLines } from "./Reveal";

export default function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Enter email and password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      onSuccess?.(data.admin);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-brand-bg px-6 overflow-hidden">
      <div className="absolute inset-0 grain opacity-30" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[60vw] h-[60vw] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(102,76,221,0.20), transparent 60%)" }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-11 h-11 rounded-full border border-brand-mint/40 flex items-center justify-center">
            <Lock size={18} className="text-brand-mint" />
          </span>
          <div>
            <p className="eyebrow">Studio Admin</p>
            <p className="text-zinc-500 text-sm mt-1">Restricted area</p>
          </div>
        </div>

        <h1 className="font-display text-5xl lg:text-6xl leading-[0.9] tracking-tighter uppercase mb-10">
          <MaskedLines lines={[<>Sign <span className="font-serif-accent text-brand-mint lowercase">in.</span></>]} />
        </h1>

        <form onSubmit={submit} className="space-y-6 border border-[color:var(--border)] p-8 bg-[color:var(--surface)]" data-testid="admin-login-form">
          <div>
            <label className="eyebrow block mb-3">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-brand-mint outline-none py-3 text-white transition-colors"
              placeholder="admin@dorado9.com"
            />
          </div>
          <div>
            <label className="eyebrow block mb-3">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-brand-mint outline-none py-3 text-white transition-colors"
              placeholder="••••••••"
            />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
            {busy ? "Signing in…" : "Enter console"} <ArrowRight size={16} />
          </button>
        </form>
      </motion.div>
    </section>
  );
}
